/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import {
	AnalyticsContext,
	AnalyticsListener,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import CollapseVerticalIcon from '@atlaskit/icon/core/collapse-vertical';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { DatasourceTableView, JIRA_LIST_OF_LINKS_DATASOURCE_ID } from '@atlaskit/link-datasource';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { durations } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	onAIAnswerDialogClick,
	onJiraNlToJqlCardLoaded,
	SmartLinksAnalyticsContextLocation,
} from '../../../../ai-answer-dialog/analytics';
import type { NonGenerativeAIAnswerResultShownProps } from '../../../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';
import { type AiBrand, AnswerCardType } from '../../../../constants';
import { sha256Hash } from '../../../../utils';
import { AIDisclaimerAndFeedback } from '../../../common/ui/ai-disclaimer-and-feedback';
import { SmartAnswerCardBodyGenerating } from '../../../common/ui/smart-answer-card-body-generating';
import { useJiraAgent } from '../../api/useJiraAgent/useJiraAgent';
import { useNaturalLanguageToJqlQuery } from '../../api/useNaturalLanguageToJqlQuery/useNaturalLanguageToJqlQuery';
import { generateJiraSAINSearchUrl } from '../utils/generateJiraSAINSearchUrl';

import { JiraNlToJqlErrorCard } from './JiraNlToJqlErrorCard';
import { messages } from './messages';
import { useJiraNlToJqlCardAnimation, TABLE_HEIGHT_COLLAPSED } from './useJiraNlToJqlCardAnimation';
import { ViewAllButton } from './ViewAllButton';

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingInline: token('space.200'),
	},
	tableContainer: {
		position: 'relative',
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		overflow: 'hidden',
	},
	viewAllContainer: {
		textAlign: 'left',
	},
	resizingContentWrapper: {
		overflow: 'hidden',
	},
});

/** Scroll area height while datasource is loading; matches collapsed resolved table height (TABLE_HEIGHT_COLLAPSED). */
const DATASOURCE_LOADING_TABLE_HEIGHT = TABLE_HEIGHT_COLLAPSED;
/** Height of DatasourceTableView footer (item count, refresh, etc.) so container wraps table + footer. */
const TABLE_FOOTER_HEIGHT = 41;
/** Stack gap (space.150) + disclaimer section so we can drive content wrapper height in sync with table expand. */
const CONTENT_BELOW_TABLE_HEIGHT = 36;
/** space.150 in px; added to wrapper height when paddingTop is used so content is not cut off (border-box). */
const CONTENT_WRAPPER_PADDING_TOP_PX = 12;

const DATASOURCE_RESOLVE_TIMEOUT_MS = 10000;
const EXPAND_BUTTON_ROW_THRESHOLD = 4;
/** When expanded and row count is in this range, use height: auto instead of fixed expanded height. */
const EXPAND_TO_AUTO_ROW_THRESHOLD = 10;

const CONTENT_WRAPPER_TRANSITION_MS = durations.large;
const CONTENT_WRAPPER_TRANSITION_EASING = 'cubic-bezier(0.15, 1, 0.3, 1)';
const CONTENT_WRAPPER_OPACITY_TRANSITION = `opacity ${CONTENT_WRAPPER_TRANSITION_MS}ms ${CONTENT_WRAPPER_TRANSITION_EASING}`;
const CONTENT_WRAPPER_HEIGHT_TRANSITION = `height ${CONTENT_WRAPPER_TRANSITION_MS}ms ${CONTENT_WRAPPER_TRANSITION_EASING}`;
const CONTENT_WRAPPER_PADDING_TOP_TRANSITION = `padding-top ${CONTENT_WRAPPER_TRANSITION_MS}ms ${CONTENT_WRAPPER_TRANSITION_EASING}`;

export const JiraNlToJqlSmartlinkCard = memo(
	(props: {
		query: string;
		cloudId: string;
		aiBrand: AiBrand;
		searchSessionId: string;
		workspaceId: string;
		product: string;
		onFMPReached: () => void;
	}) => {
		const renderStartTime = useRef(performance.now());
		const analyticsFiredRef = useRef(false);
		const { query, cloudId, aiBrand, searchSessionId, workspaceId, product, onFMPReached } = props;
		const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
		const [showExpandButton, setShowExpandButton] = useState(false);
		const [tableRowCount, setTableRowCount] = useState(0);
		const [isDatasourceResolved, setIsDatasourceResolved] = useState(false);
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { trackAIInteractionInit } = useAIEventsInstrumentation();
		const { commonAttributes, setData, traceHeaders } = useAIAnswerContext();

		const animationsEnabled = fg('kd_sain_jira_card_animations');
		const expandToAuto = showExpandButton && tableRowCount <= EXPAND_TO_AUTO_ROW_THRESHOLD;
		const animation = useJiraNlToJqlCardAnimation(animationsEnabled, {
			expandToAuto,
		});

		const useJiraAgentApi = fg('kd_sain_jira_card_migrate_jira_agent');
		const jiraAgentResult = useJiraAgent({
			query,
			cloudId,
			workspaceId,
			product,
			traceHeaders,
			enabled: useJiraAgentApi,
		});
		const nlResult = useNaturalLanguageToJqlQuery({
			cloudId,
			query,
			workspaceId,
			product,
			enabled: !useJiraAgentApi,
		});

		const jqlLoading = useJiraAgentApi ? jiraAgentResult.loading : nlResult.loading;
		const jqlError = useJiraAgentApi ? jiraAgentResult.error : nlResult.error;
		const jql = useJiraAgentApi ? jiraAgentResult.jql : nlResult.jql;

		const jiraSearchUrl = useMemo(
			() => (jql ? generateJiraSAINSearchUrl(query, jql) : null),
			[jql, query],
		);

		/** URL for "View all work items" on the error card (no JQL, search by query text only). */
		const errorCardViewAllUrl = useMemo(() => generateJiraSAINSearchUrl(query), [query]);

		const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				query,
				answerString: jql ? sha256Hash(jql) : '',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
			}),
			[searchSessionId, query, aiBrand, jql],
		);

		const analyticsAttributesWithoutUGC = useMemo<
			Omit<NonGenerativeAIAnswerResultShownProps, 'query' | 'answerString'>
		>(
			() => ({
				searchSessionId,
				queryHash: sha256Hash(query),
				brand: aiBrand,
				source: 'jira_nl_2_jql_card',
				answerCardType: AnswerCardType.JIRA_NATURAL_LANGUAGE_QUERY,
			}),
			[searchSessionId, query, aiBrand],
		);

		const fireJiraNlToJqlCardLoaded = useCallback(
			({
				success,
				endState,
			}: {
				success: boolean;
				endState?: 'answered' | 'no-issues' | 'jql-error' | 'issues-error' | 'datasource-timeout';
			}) => {
				const apiLoadedTime = performance.now();
				const totalDuration = apiLoadedTime - renderStartTime.current;
				fireAnalyticsEvent(
					onJiraNlToJqlCardLoaded({
						...analyticsAttributes,
						success,
						loadTime: totalDuration,
						endState,
						jiraNlToJqlCardVariant: 'smartlink',
					}),
				);
			},
			[fireAnalyticsEvent, analyticsAttributes],
		);

		const updateDOMDerivedState = useCallback(() => {
			const el = tableScrollContainerRef.current;
			if (!el) {
				return;
			}
			const dataRows = el.querySelectorAll('table tbody tr');
			const count = dataRows.length;
			setTableRowCount(count);
			setShowExpandButton(count > EXPAND_BUTTON_ROW_THRESHOLD);
		}, []);

		useEffect(() => {
			const el = tableScrollContainerRef.current;
			if (!el) {
				return;
			}
			updateDOMDerivedState();
			// Debounce added to prevent excessive re-renders and wait for the DOM to settle
			let debounceId: ReturnType<typeof setTimeout> | null = null;
			const observer = new MutationObserver(() => {
				if (debounceId !== null) {
					clearTimeout(debounceId);
				}
				debounceId = setTimeout(() => {
					debounceId = null;
					updateDOMDerivedState();
				}, 150);
			});
			observer.observe(el, { childList: true, subtree: true });
			return () => {
				if (debounceId !== null) {
					clearTimeout(debounceId);
				}
				observer.disconnect();
			};
		}, [animation.isExpanded, jql, updateDOMDerivedState]);

		useEffect(
			() => {
				trackAIInteractionInit();
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);

		// fire Analytics Events on final state changes
		// Waits for isDatasourceResolved so loadTime includes the DatasourceTableView's own API fetch,
		// giving an accurate TTI/FMP that covers both the NL→JQL call and the issue search.
		useEffect(() => {
			if (analyticsFiredRef.current) {
				return;
			}

			if (!jqlLoading && !jqlError && jql && isDatasourceResolved) {
				analyticsFiredRef.current = true;
				onFMPReached();
				fireJiraNlToJqlCardLoaded({ success: true, endState: 'answered' });
				if (fg('kd_fix_nl2jql_mau_instrumentation')) {
					fireSAINUsageEvent({
						commonAttributes,
					});
				}
			} else if (!jqlLoading && jqlError) {
				analyticsFiredRef.current = true;
				fireJiraNlToJqlCardLoaded({ success: false, endState: 'jql-error' });
			}
		}, [
			jqlLoading,
			jqlError,
			jql,
			isDatasourceResolved,
			onFMPReached,
			fireJiraNlToJqlCardLoaded,
			fireSAINUsageEvent,
			commonAttributes,
		]);

		useEffect(() => {
			if (isDatasourceResolved || jqlLoading || jqlError || !jql) {
				return;
			}
			const id = setTimeout(() => {
				if (!analyticsFiredRef.current) {
					analyticsFiredRef.current = true;
					fireJiraNlToJqlCardLoaded({ success: false, endState: 'datasource-timeout' });
				}
				setIsDatasourceResolved(true);
			}, DATASOURCE_RESOLVE_TIMEOUT_MS);
			return () => clearTimeout(id);
		}, [isDatasourceResolved, jqlLoading, jqlError, jql, fireJiraNlToJqlCardLoaded]);

		// Set debug data for online debugger when API response is available
		// Separate from analytics effect to avoid infinite loop with commonAttributes
		useEffect(() => {
			if (!jqlLoading && jql && fg('kd_sain_online_debugger')) {
				setData({
					errorState: null,
					format: null,
					uniqueSources: [],
					disclaimer: null,
					extraAPIAnalyticsAttributes: {
						traceId: commonAttributes.extraAttributes?.requestId ?? null,
						requestId: commonAttributes.extraAttributes?.requestId ?? null,
					},
				});
			}
		}, [jqlLoading, jql, setData, commonAttributes.extraAttributes?.requestId]);

		const fireEngagementEvent = useCallback(
			(sainAction: SainAction) => {
				fireSAINUsageEvent({ metCriteria: sainAction, commonAttributes });
			},
			[fireSAINUsageEvent, commonAttributes],
		);

		const handleDatasourceAnalyticsEvent = useCallback(
			(event: UIAnalyticsEvent) => {
				const { payload } = event;

				if (payload.action === 'renderSuccess' && payload.actionSubject === 'datasource') {
					setIsDatasourceResolved(true);
				}

				if (payload.action === 'clicked') {
					if (fg('kd_fix_nl2jql_mau_instrumentation')) {
						if (payload.actionSubject === 'link') {
							fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_WORK_ITEM_CLICKED);
							fireAnalyticsEvent(
								onAIAnswerDialogClick({
									...commonAttributes,
									elementInteracted: 'jira-issue-key',
									answerString: jql ? sha256Hash(jql) : '',
									jiraNlToJqlCardVariant: 'smartlink',
									isAuxClick: payload.attributes?.clickType === 'middle',
								}),
							);
						} else {
							fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_CLICKED);
							fireAnalyticsEvent(
								onAIAnswerDialogClick({
									...commonAttributes,
									elementInteracted: payload.actionSubject,
									jiraNlToJqlCardVariant: 'smartlink',
								}),
							);
						}
					}
				}
			},
			[fireAnalyticsEvent, commonAttributes, fireEngagementEvent, jql],
		);

		const onViewAllButtonClicked = useCallback(
			(e?: React.MouseEvent) => {
				const isAuxClick = e?.button === 1;
				if (isAuxClick && !SAINFeatureControl.isMiddleClickTrackingEnabled()) {
					return;
				}
				fireAnalyticsEvent(
					onAIAnswerDialogClick({
						...commonAttributes,
						elementInteracted: 'jira-view-all-button',
						answerString: jql ? sha256Hash(jql) : '',
						jiraNlToJqlCardVariant: 'smartlink',
						isAuxClick,
					}),
				);
				if (fg('kd_fix_nl2jql_mau_instrumentation')) {
					fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_VIEW_ALL);
				}
			},
			[fireAnalyticsEvent, fireEngagementEvent, commonAttributes, jql],
		);

		const onExpandButtonClicked = useCallback(() => {
			animation.onExpandClick();
			fireAnalyticsEvent(
				aiAnswerExpandClicked({ ...commonAttributes, jiraNlToJqlCardVariant: 'smartlink' }),
			);
			fireEngagementEvent(SainAction.EXPAND_CLICKED);
		}, [animation, fireAnalyticsEvent, commonAttributes, fireEngagementEvent]);

		const onCollapseButtonClicked = useCallback(() => {
			animation.onCollapseClick();
			fireAnalyticsEvent(
				aiAnswerCollapseClicked({ ...commonAttributes, jiraNlToJqlCardVariant: 'smartlink' }),
			);
		}, [animation, fireAnalyticsEvent, commonAttributes]);

		const onHideButtonClicked = useCallback(() => {
			animation.onHideClick();
			fireAnalyticsEvent(
				onAIAnswerDialogClick({
					...commonAttributes,
					elementInteracted: 'jira-hide-table-button',
					jiraNlToJqlCardVariant: 'smartlink',
				}),
			);
		}, [animation, fireAnalyticsEvent, commonAttributes]);

		const onRevealButtonClicked = useCallback(() => {
			animation.onRevealClick();
			fireAnalyticsEvent(
				onAIAnswerDialogClick({
					...commonAttributes,
					elementInteracted: 'jira-reveal-table-button',
					jiraNlToJqlCardVariant: 'smartlink',
				}),
			);
		}, [animation, fireAnalyticsEvent, commonAttributes]);

		if (jqlLoading) {
			return (
				<AnimatedWrapper>
					<SmartAnswerCardBodyGenerating />
				</AnimatedWrapper>
			);
		}

		if (jqlError || !jql) {
			return (
				<JiraNlToJqlErrorCard
					query={query}
					viewAllUrl={errorCardViewAllUrl}
					onViewAllClick={onViewAllButtonClicked}
				/>
			);
		}

		// When expanded with 5–10 rows, use height auto so the table isn't stuck at a fixed tall height
		const useAutoHeightWhenExpanded = expandToAuto && animation.isExpanded;

		const useFixedTableHeight =
			!isDatasourceResolved || (showExpandButton && !useAutoHeightWhenExpanded);
		const tableScrollHeight = isDatasourceResolved
			? animation.animatedTableHeight
			: DATASOURCE_LOADING_TABLE_HEIGHT;

		const tableContainerHeight = useFixedTableHeight
			? tableScrollHeight + TABLE_FOOTER_HEIGHT
			: 'auto';
		const scrollableContainerHeight = useFixedTableHeight ? tableScrollHeight : undefined;

		const contentWrapperHeight =
			animation.revealHeightZero || animation.isHidden
				? 0
				: useFixedTableHeight
					? tableScrollHeight +
						TABLE_FOOTER_HEIGHT +
						CONTENT_BELOW_TABLE_HEIGHT +
						CONTENT_WRAPPER_PADDING_TOP_PX
					: 'auto';

		const headerSection = (
			<Inline spread="space-between" alignBlock="center">
				<Inline alignBlock="center" space="space.075">
					<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
					<Heading size="xsmall" as="h2">
						{formatMessage(messages.smartlinkCardTitle)}
					</Heading>
				</Inline>
				<Inline space="space.100" alignBlock="center">
					{jiraSearchUrl ? (
						<Box xcss={styles.viewAllContainer}>
							<ViewAllButton
								url={jiraSearchUrl}
								callback={onViewAllButtonClicked}
								spacing="compact"
							/>
						</Box>
					) : null}
					{!animation.isHidden && showExpandButton && (
						<IconButton
							appearance="default"
							onClick={animation.isExpanded ? onCollapseButtonClicked : onExpandButtonClicked}
							testId="jira-sain-card-expand-collapse-button"
							icon={animation.isExpanded ? CollapseVerticalIcon : GrowVerticalIcon}
							spacing="compact"
							label={
								animation.isExpanded
									? formatMessage(messages.collapse)
									: formatMessage(messages.expand)
							}
							isTooltipDisabled={false}
						/>
					)}
					<IconButton
						appearance="default"
						onClick={animation.isHidden ? onRevealButtonClicked : onHideButtonClicked}
						testId={
							animation.isHidden ? 'jira-sain-card-reveal-button' : 'jira-sain-card-hide-button'
						}
						icon={animation.isHidden ? EyeOpenIcon : EyeOpenStrikethroughIcon}
						spacing="compact"
						label={
							animation.isHidden ? formatMessage(messages.unhide) : formatMessage(messages.hide)
						}
						isTooltipDisabled={false}
					/>
				</Inline>
			</Inline>
		);

		return (
			<Stack xcss={styles.container} testId="jira-nl-to-jql-card-wrapper">
				{headerSection}
				<Box
					xcss={styles.resizingContentWrapper}
					testId="jira-nl-to-jql-content-wrapper"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- height, padding-top + transition driven for hide/reveal and expand/collapse sync
					style={{
						paddingTop: animation.revealHeightZero || animation.isHidden ? 0 : token('space.150'),
						// this is a false positive, the height is not static
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						height: contentWrapperHeight,
						transition:
							animationsEnabled && (animation.isHidden || animation.contentWrapperTransitioning)
								? `${CONTENT_WRAPPER_HEIGHT_TRANSITION}, ${CONTENT_WRAPPER_PADDING_TOP_TRANSITION}`
								: 'none',
					}}
					onTransitionEnd={animation.onContentWrapperTransitionEnd}
				>
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- opacity transition for hide/reveal
						style={{
							opacity: animationsEnabled && (animation.isHidden || animation.justRevealed) ? 0 : 1,
							transition:
								animationsEnabled && (animation.isHidden || animation.contentWrapperTransitioning)
									? CONTENT_WRAPPER_OPACITY_TRANSITION
									: 'none',
						}}
					>
						<Stack space="space.150">
							<Box
								xcss={styles.tableContainer}
								// this is a false positive, the height is not static
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
								style={{ height: tableContainerHeight }}
								ref={tableScrollContainerRef}
							>
								<AnalyticsListener channel="media" onEvent={handleDatasourceAnalyticsEvent}>
									<AnalyticsContext
										data={{
											attributes: {
												location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
												...analyticsAttributesWithoutUGC,
											},
											location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_NL_TO_JQL_CARD,
										}}
									>
										<SmartCardProvider>
											{/* reference: https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/linking-platform/link-datasource/examples-helpers/buildJiraIssuesTable.tsx */}
											<DatasourceTableView
												datasourceId={JIRA_LIST_OF_LINKS_DATASOURCE_ID}
												parameters={{
													cloudId,
													jql,
												}}
												scrollableContainerHeight={scrollableContainerHeight}
											/>
										</SmartCardProvider>
									</AnalyticsContext>
								</AnalyticsListener>
							</Box>
							<Box>
								<AIDisclaimerAndFeedback
									showFeedbackButtons={true}
									showCopyButton
									copyText={jiraSearchUrl ?? ''}
								/>
							</Box>
						</Stack>
					</Box>
				</Box>
			</Stack>
		);
	},
);
